// Debug Logger - Captures errors for debugging without visual inspection
// SIMPLIFIED VERSION - No fetch interception to avoid infinite loops
import { CONFIG } from "@/config";

interface LogEntry {
  timestamp: string;
  event: string;
  data: any;
  url: string;
  sessionId: string;
}

class DebugLogger {
  private sessionId: string;
  private logs: LogEntry[] = [];
  private maxLogs = 100;
  private isInitialized = false;

  constructor() {
    this.sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // Don't auto-initialize to prevent loops
  }

  init() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.loadLogs();
    this.attachListeners();
    console.log("[DebugLogger] Initialized");
  }

  private loadLogs() {
    try {
      const stored = localStorage.getItem("debug_logs");
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (e) {
      // Silently fail - don't log errors while loading logs!
    }
  }

  private saveLogs() {
    try {
      localStorage.setItem("debug_logs", JSON.stringify(this.logs));
    } catch (e) {
      // Silently fail
    }
  }

  write(event: string, data: any) {
    // Prevent recursive logging
    if (event.includes("LOGGER")) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      event,
      data,
      url: window.location.href,
      sessionId: this.sessionId,
    };

    this.logs.push(entry);

    // Keep only last N logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    this.saveLogs();

    // Only log to console in dev, and only important events
    if (CONFIG.IS_DEVELOPMENT && event.includes("ERROR")) {
      console.log(`[DEBUG] ${event}:`, data);
    }
  }

  dump(): string {
    const logData = JSON.stringify(this.logs, null, 2);

    // Create downloadable file
    const blob = new Blob([logData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `.debug-log-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);

    return logData;
  }

  clear() {
    this.logs = [];
    this.saveLogs();
    console.log("[DebugLogger] Logs cleared");
  }

  getLast(n: number = 10): LogEntry[] {
    return this.logs.slice(-n);
  }

  getErrors(): LogEntry[] {
    return this.logs.filter(
      log => log.event.includes("ERROR") || log.event.includes("REJECTION") || log.event.includes("FAIL"),
    );
  }

  private attachListeners() {
    // Capture window errors
    window.addEventListener("error", e => {
      // Prevent logging our own errors
      if (e.filename?.includes("debug-logger")) return;

      this.write("WINDOW_ERROR", {
        message: e.message,
        filename: e.filename,
        line: e.lineno,
        col: e.colno,
      });
    });

    // Capture unhandled promise rejections
    window.addEventListener("unhandledrejection", e => {
      this.write("PROMISE_REJECTION", {
        reason: e.reason?.message || e.reason,
        type: typeof e.reason,
      });
    });

    // Simple navigation tracking
    window.addEventListener("popstate", () => {
      this.write("NAVIGATION", {
        to: window.location.pathname,
      });
    });

    // NO FETCH INTERCEPTION - This was causing the infinite loop!
    // We'll rely on the app's error handling to log API failures
  }

  // Get summary for validation script
  getSummary() {
    const errors = this.getErrors();
    const last10 = this.getLast(10);

    return {
      sessionId: this.sessionId,
      totalLogs: this.logs.length,
      errorCount: errors.length,
      lastError: errors[errors.length - 1] || null,
      recentEvents: last10.map(l => ({
        time: l.timestamp,
        event: l.event,
        url: l.url,
      })),
    };
  }
}

// Create singleton instance
const debugLog = new DebugLogger();

// Make available globally for debugging
if (typeof window !== "undefined") {
  (window as any).debugLog = debugLog;

  // Only initialize after a delay to avoid startup issues
  setTimeout(() => {
    debugLog.init();
  }, 100);
}

export { debugLog };
export type { LogEntry };
